/**
 * OC Theme System
 * Provides 4 default theme configurations:
 * 1. Light theme with SVG background
 * 2. Light theme without SVG background
 * 3. Dark theme with SVG background
 * 4. Dark theme without SVG background
 * 
 * Customers can override any color values via the theme prop
 */

import colors from '@hl/ui-components/styles/colors.scss';

import { IAppTheme } from '../../../styles/IAppTheme';
import { appTheme } from '../../../styles/Theme';

/**
 * OC-Specific Color Palette
 */
export const OcColors = {
  // OC colors
  defaultText: '#292929',   // Comparable to defaultText in HL design system
  metaText: '#606065',      // Comparable to metaText in HL design system
  bg2: '#efefef',           // Comparable to bg2 in HL design system
  bg3: '#dededf',           // Comparable to bg3 in HL design system

  userMessageBG: '#e1eefb', // Comparable to hard-coded user message bg HL chat design
  
  link: '#0B57D0',
  linkHover: '#0B439D',
  
  // Skeleton loading gradients
  skeletonLight1: 'rgba(240, 240, 240, 0.25)',
  skeletonLight2: 'rgba(100, 100, 100, 0.2)',
  
  // Dark theme colors
  // darkBg1: '#2a2a2a',            // Darkest background
  darkBg2: '#3a3a3a',            // Medium dark background
  darkBg3: '#4c4c4c',            // Lighter dark background
  darkBg4: '#5a5a5a',            // Even lighter (hover states)
} as const;

/**
 * OC-specific theme extension with additional colors and sizes for OC widget components
 */
export interface IOcAppTheme extends IAppTheme {
  color: IAppTheme['color'] & {
    /** Avatar primary color (main color) */
    avatarColor?: string;
    /** Avatar secondary color (inner sparkles) */
    avatarSecondaryColor?: string;

    /** Source link text color for hover/focus states */
    sourceLinkHover?: string;

    /** Primary text color on chatBG */
    textPrimary?: string;
    /** Tertiary/muted text color on chatBG */
    textMuted?: string;
    
    /** Secondary background color (ask human footer, selected history item, sources ordinal column) */
    secondaryBG?: string;
    /** Text color on secondary background color */
    textSecondary?: string;

    /** Button background color (ask human, new chat) */
    buttonBG?: string;
    /** Button text color */
    buttonText?: string;
    /** If buttons should highlight or darken on action (ex: button hover) */
    highlightOnButtonBG?: boolean;
    /** If items on the chat panel background should highlight or darken on action (ex: button hover, scroll bar) */
    highlightOnChatPanelBG?: boolean;
    /** If items on the chat background should highlight or darken on action (ex: button hover, scroll bar) */
    highlightOnChatBG?: boolean;
    /** If items on svg elements should highlight or darken on action (ex: button hover) */
    highlightOnSVG?: boolean;

    /** Skeleton gradient light color (start/stop color) */
    loadingBackgroundGradientLight?: string;
    /** Skeleton gradient dark color (start/stop color) */
    loadingBackgroundGradientDark?: string;
  };
  font: IAppTheme['font'] & {
    size: IAppTheme['font']['size'] & {
      xSmall: string;
    };
  };
}

// Get the base themes from Theme.ts
const baseThemes = appTheme();

/**
 * Light theme WITH SVG background
 * Optimized for use with decorative SVG wave background
 */
export const OC_LIGHT_THEME_WITH_SVG: IOcAppTheme = {
  ...baseThemes.light,
  color: {
    ...baseThemes.light.color,
    chatPanel: colors.hlOrange,
    chatBorder: colors.ltGray,
    assistantMessage: OcColors.defaultText,
    userMessage: OcColors.defaultText,
    userMessageBG: OcColors.userMessageBG,
    messageInputButton: OcColors.defaultText,
    reaction: OcColors.defaultText,
    retryButton: OcColors.link,
    errorLink: OcColors.link,
    errorLinkHover: OcColors.linkHover,
    // override with OC badge colors to use as default badge colors
    betaBadgeBG: OcColors.bg2,
    betaBadge: OcColors.metaText,

    // OC-specific colors
    avatarColor: colors.hlOrange, // defaults to chatPanelBG
    avatarSecondaryColor: colors.white, // defaults to chatPanel
    buttonBG: colors.hlOrange, // defaults to chatPanelBG
    buttonText: colors.white, // defaults to chatPanel
    sourceLinkHover: OcColors.linkHover, // defaults to buttonBG || chatPanelBG

    textPrimary: OcColors.defaultText, // defaults to OcColors.defaultText
    textMuted: OcColors.metaText, // defaults to OcColors.metaText
    secondaryBG: OcColors.bg2, // defaults to OcColors.bg2
    textSecondary: OcColors.defaultText, // defaults to OcColors.defaultText

    highlightOnButtonBG: false,
    highlightOnChatPanelBG: false,
    highlightOnChatBG: false,
    highlightOnSVG: true,

    loadingBackgroundGradientLight: OcColors.skeletonLight1,
    loadingBackgroundGradientDark: OcColors.skeletonLight2,
  },
  font: {
    ...baseThemes.light.font,
    size: {
      ...baseThemes.light.font.size,
      xSmall: '12px',
      small: '13px',
      medium: '14px',
      large: '20px',
    },
    weight: {
      ...baseThemes.light.font.weight,
      semiBold: 580
    },
  },
};

/**
 * Light theme WITHOUT SVG background
 * Plain light theme without decorative background
 */
export const OC_LIGHT_THEME_NO_SVG: IOcAppTheme = {
  ...OC_LIGHT_THEME_WITH_SVG,
  color: {
    ...OC_LIGHT_THEME_WITH_SVG.color,
    chatPanel: colors.darkestGray,
    highlightOnSVG: false,
  }
};

/**
 * Dark theme WITH SVG background
 * Optimized for use with decorative SVG wave background
 */
export const OC_DARK_THEME_WITH_SVG: IOcAppTheme = {
  ...OC_LIGHT_THEME_WITH_SVG,
  color: {
    ...OC_LIGHT_THEME_WITH_SVG.color,
    chatPanelBG: OcColors.darkBg3,
    chatPanel: colors.white,
    chatBg: OcColors.darkBg2,
    chatHeading: colors.white,
    chatBorder: OcColors.darkBg3,
    assistantMessageBG: OcColors.darkBg4,
    assistantMessage: colors.white,
    reaction: colors.white,
    messageInputButton: colors.white,
    
    // OC-specific colors
    sourceLinkHover: colors.hlOrange,
    textPrimary: colors.white,
    textMuted: colors.ltestGray,
    secondaryBG: colors.medGray,
    highlightOnChatPanelBG: true,
    highlightOnChatBG: true,
  }
};

/**
 * Dark theme WITHOUT SVG background
 * Plain dark theme without decorative background
 */
export const OC_DARK_THEME_NO_SVG: IOcAppTheme = {
  ...OC_DARK_THEME_WITH_SVG
};

/**
 * Legacy theme exports for backward compatibility
 * These map to the "with SVG" variants by default
 */
export const OC_DEFAULT_THEME = OC_LIGHT_THEME_WITH_SVG;
export const OC_DARK_THEME = OC_DARK_THEME_WITH_SVG;

/**
 * Helper function to get the appropriate theme based on options
 */
export function getOcTheme(themeMode: 'light' | 'dark', useSVGBackground: boolean): IOcAppTheme {
  if (themeMode === 'dark') {
    return useSVGBackground ? OC_DARK_THEME_WITH_SVG : OC_DARK_THEME_NO_SVG;
  }
  return useSVGBackground ? OC_LIGHT_THEME_WITH_SVG : OC_LIGHT_THEME_NO_SVG;
}
